import * as React from 'react';
import classNames from 'classnames';
import { getMilFilteredFacetsAsync } from '../../../actions/DataActionExtension.g';
import { IGetFacetValueResponse } from '../../../actions/DataServiceEntities.g';
import { Button } from '@msdyn365-commerce-modules/utilities';

export interface IMilFilterRefinerProps {
    facetName: string;
    searchTerm?: string;
    selectedRefiners?: Record<string, string[]>;
    actionContext?: any;
    onResults?: (results: IGetFacetValueResponse | undefined) => void;
    debounceMs?: number;
    className?: string;
    minChars?: number;
    onSearchTextChange: (facetName: string, text: string) => void;
    externalText?: string;
}

function buildFiltersArray(selectedRefiners?: Record<string, string[]>): string[] {
    if (!selectedRefiners) {
        return [];
    }
    const arr: string[] = [];
    Object.keys(selectedRefiners).forEach(facet => {
        const values = selectedRefiners[facet] || [];
        values.forEach(v => {
            if (v !== undefined && v !== null && `${v}`.trim() !== '') {
                arr.push(`${facet}:${v}`);
            }
        });
    });
    return arr;
}

const MilFilterRefiner: React.FC<IMilFilterRefinerProps> = ({
    facetName,
    searchTerm,
    selectedRefiners,
    actionContext,
    onResults,
    debounceMs = 400,
    className,
    minChars = 1,
    onSearchTextChange,
    externalText = ''
}) => {
    const [value, setValue] = React.useState<string>('');

    const timerRef = React.useRef<number | null>(null);
    const requestTokenRef = React.useRef<number>(0);
    const mountedRef = React.useRef<boolean>(true);

    const selectedRefinersRef = React.useRef<Record<string, string[]> | undefined>(selectedRefiners);
    const actionContextRef = React.useRef<any>(actionContext);
    const searchTermRef = React.useRef<string | undefined>(searchTerm);

    React.useEffect(() => {
        if (externalText !== undefined && externalText !== value) {
            setValue(externalText);
            if ((externalText ?? '').trim() === '') {
                onResults?.(undefined);
            }
        }
    }, [externalText]);

    React.useEffect(() => {
        selectedRefinersRef.current = selectedRefiners;
    }, [selectedRefiners]);

    React.useEffect(() => {
        actionContextRef.current = actionContext;
    }, [actionContext]);

    React.useEffect(() => {
        searchTermRef.current = searchTerm;
    }, [searchTerm]);

    React.useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            if (timerRef.current) {
                window.clearTimeout(timerRef.current);
            }
            requestTokenRef.current++;
        };
    }, []);

    const doFetch = async (typedText: string) => {
        const ctx = actionContextRef.current;
        if (!ctx) {
            return;
        }

        const filters = buildFiltersArray(selectedRefinersRef.current);

        requestTokenRef.current++;
        const myToken = requestTokenRef.current;

        try {
            const resp = await getMilFilteredFacetsAsync(
                { callerContext: ctx },
                searchTermRef.current ?? '',
                facetName,
                typedText,
                filters
            );

            if (!mountedRef.current || myToken !== requestTokenRef.current) {
                return;
            }
            onResults?.(resp);
        } catch (err) {
            if (!mountedRef.current || myToken !== requestTokenRef.current) {
                return;
            }
            onResults?.(undefined);
        }
    };

    React.useEffect(() => {
        if (timerRef.current) {
            window.clearTimeout(timerRef.current);
            timerRef.current = null;
        }

        const typed = value.trim();
        const shouldSchedule = typed.length >= minChars;

        if (!shouldSchedule) {
            return;
        }

        timerRef.current = window.setTimeout(() => {
            void doFetch(typed);
            timerRef.current = null;
        }, debounceMs);

        return () => {
            if (timerRef.current) {
                window.clearTimeout(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [value, debounceMs, minChars, facetName]);

    const onKeyDown: React.KeyboardEventHandler<HTMLInputElement> = e => {
        if (e.key === 'Enter') {
            if (timerRef.current) {
                window.clearTimeout(timerRef.current);
                timerRef.current = null;
            }
            void doFetch(value.trim());
        }
    };

    return (
        <div className={classNames('mil-refine-menu__filter-input', className)}>
            <input
                id={facetName}
                type='text'
                value={value}
                onChange={e => {
                    const newVal = e.target.value;
                    setValue(newVal);
                    onSearchTextChange?.(facetName, newVal);
                    if (newVal === '') {
                        onResults?.(undefined);
                    }
                }}
                onKeyDown={onKeyDown}
                placeholder={`Find a ${facetName.toLowerCase()}`}
                aria-label={`Find a ${facetName}`}
            />
            {value && (
                <Button
                    className='mil-refine-menu__filter-input__button msc-btn--icon'
                    aria-label={`Clear filter ${facetName}`}
                    onClick={() => {
                        setValue('');
                        onSearchTextChange?.(facetName, '');
                        onResults?.(undefined);
                    }}
                ></Button>
            )}
        </div>
    );
};

export default MilFilterRefiner;
